import { CAMPAIGN_STATUS } from '@/client/constant'

/**
 * ステータス判定に必要な入力。
 * `entryActionType` はトップレベルの種別（`TCampaign.entryActionType`）を正とする。
 */
export type CampaignStatusLabelInput = {
  status: number | null | undefined
  entryActionType: string | null | undefined
  applicantsCount: number
}

/**
 * ステータス表示の描画に必要な要素。
 * `lines` は改行区切りの表示行（`<br />` で結合して描画する）。
 */
export type CampaignStatusLabel = {
  textClass: string
  lines: string[]
}

// 2 段階分岐の対象となるリポスト/引用系 3 種（requirements C1-003）
const REPOST_ENTRY_ACTION_TYPES = ['retweet', 'quote_tweet', 'retweet_and_quote_tweet']

// 「開催中」に対応する CAMPAIGN_STATUS 値（1=応募収集待ち / 2=応募収集中）
const DURING_EVENT_STATUSES: number[] = [CAMPAIGN_STATUS.BEFORE_COLLECTION, CAMPAIGN_STATUS.DURING_COLLECTION]

// 既存 CAMPAIGN_STATUS のラベル・表示クラス（他種別・他ステータスで維持する表示）
const LEGACY_STATUS_LABELS: Record<number, CampaignStatusLabel> = {
  [CAMPAIGN_STATUS.BEFORE_EVENT]: { textClass: 'text-disabled', lines: ['開催前'] },
  [CAMPAIGN_STATUS.BEFORE_COLLECTION]: { textClass: 'text-primary', lines: ['開催中', '(応募収集待ち)'] },
  [CAMPAIGN_STATUS.DURING_COLLECTION]: { textClass: 'text-primary', lines: ['開催中', '(応募収集中)'] },
  [CAMPAIGN_STATUS.DURING_FINAL_COLLECTION]: { textClass: 'text-success', lines: ['開催終了', '(最終集計中)'] },
  [CAMPAIGN_STATUS.COLLECTION_COMPLETED]: { textClass: 'text-success', lines: ['開催終了', '(全データ集計完了)'] }
}

const isRepostEntryActionType = (entryActionType: string | null | undefined): boolean =>
  entryActionType != null && REPOST_ENTRY_ACTION_TYPES.includes(entryActionType)

const isDuringEventStatus = (status: number | null | undefined): boolean =>
  status != null && DURING_EVENT_STATUSES.includes(status)

/**
 * キャンペーンステータスの表示ラベルを判定する。
 *
 * - リポスト/引用系 3 種 かつ「開催中」ステータスのときは応募者数で分岐する。
 *   - 応募者 1 件以上 → 「開催中(応募収集中)」
 *   - 応募者 0 件 → 「開催中(応募収集待ち)」
 * - その他種別・その他ステータス（開催前 / 開催終了系）は既存 CAMPAIGN_STATUS の表示を維持する。
 * - CAMPAIGN_STATUS に該当しない値は null（描画しない）。
 */
export const resolveCampaignStatusLabel = (input: CampaignStatusLabelInput): CampaignStatusLabel | null => {
  const { status, entryActionType, applicantsCount } = input

  if (isRepostEntryActionType(entryActionType) && isDuringEventStatus(status)) {
    return applicantsCount >= 1
      ? LEGACY_STATUS_LABELS[CAMPAIGN_STATUS.DURING_COLLECTION]
      : LEGACY_STATUS_LABELS[CAMPAIGN_STATUS.BEFORE_COLLECTION]
  }

  if (status != null && Object.prototype.hasOwnProperty.call(LEGACY_STATUS_LABELS, status)) {
    return LEGACY_STATUS_LABELS[status]
  }

  return null
}
